import { DOCUMENT, Injectable, inject, signal } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { ToastrService } from 'ngx-toastr';
import { filter, interval } from 'rxjs';

/** How often the app asks the server whether a new version has been deployed. */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Keeps an installed app up to date.
 *
 * A home screen app can stay open for days, so unlike a browser tab it never picks up a deployment
 * on its own. This service polls for new versions, tells the user when one is ready, and reloads the
 * app when an unrecoverable cache state is detected.
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
    private readonly _document = inject(DOCUMENT);
    private readonly _swUpdate = inject(SwUpdate);
    private readonly _toastr = inject(ToastrService);

    private readonly _updateReady = signal<boolean>(false);

    /** True once a new version has been downloaded and only needs a reload to be activated. */
    readonly updateReady = this._updateReady.asReadonly();

    init(): void {
        if (!this._swUpdate.isEnabled) {
            return;
        }

        this._swUpdate.versionUpdates
            .pipe(filter((event: VersionEvent) => event.type === 'VERSION_READY'))
            .subscribe(() => {
                this._updateReady.set(true);
                this._notifyUpdateReady();
            });

        // The service worker could not find the files of the version it was serving (for instance
        // after a deployment removed the hashed bundles). Reloading is the only way out.
        this._swUpdate.unrecoverable.subscribe(() => {
            this._document.defaultView?.location.reload();
        });

        // Reaching the app through the network at least once tells us whether a deployment happened
        // while the app was sitting on the home screen.
        this._checkForUpdate();

        interval(UPDATE_CHECK_INTERVAL_MS).subscribe(() => this._checkForUpdate());

        this._document.addEventListener('visibilitychange', () => {
            if (this._document.visibilityState === 'visible') {
                this._checkForUpdate();
            }
        });
    }

    /** Activates the downloaded version and reloads the app on it. */
    async applyUpdate(): Promise<void> {
        if (!this._swUpdate.isEnabled) {
            return;
        }

        await this._swUpdate.activateUpdate();
        this._document.defaultView?.location.reload();
    }

    /** A check that fails (offline, server unreachable) is retried on the next tick, never surfaced. */
    private _checkForUpdate(): void {
        this._swUpdate.checkForUpdate().catch(() => undefined);
    }

    private _notifyUpdateReady(): void {
        this._toastr
            .info(
                'Une nouvelle version de l\'espace client est disponible. Touchez ici pour la charger.',
                'Mise à jour disponible',
                {
                    disableTimeOut: true,
                    tapToDismiss: false,
                    closeButton: true,
                }
            )
            .onTap.subscribe(() => {
                void this.applyUpdate();
            });
    }
}
